"use client";

import type * as React from "react";
import { Box, type BoxProps } from "../../components/ui/box";
import { CountSticker } from "../../components/ui/count-sticker";
import {
  Icon,
  type IconName,
  type IconSize,
} from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";

/** Chip dimensions used by layout code that measures a vertical rail. */
export const APP_SHELL_RAIL_TILE_CHIP_SIZE = {
  default: 32,
  lg: 36,
} as const;

const RAIL_TILE_ICON_SIZE = {
  default: "md",
  lg: "lg",
} as const satisfies Record<keyof typeof APP_SHELL_RAIL_TILE_CHIP_SIZE, IconSize>;

export type AppShellRailTileSize = keyof typeof APP_SHELL_RAIL_TILE_CHIP_SIZE;

export type AppShellRailTileProps = Omittable<{
  /** Glyph for the tile chip. Omit when `visual` supplies the mark. */
  icon?: IconName;
  /** Glyph that replaces `icon` while this tile is active. */
  selectedIcon?: IconName;
  /** Content that replaces the glyph, such as an avatar. */
  visual?: React.ReactNode;
  /** Accessible name and visible caption. */
  label: string;
  /** Optional count or short status text. */
  badge?: string;
  /** Draw `badge` as a bare presence dot instead of the count. */
  badgeDot?: boolean;
  /** Whether the tile identifies the active destination. */
  active?: boolean;
  /** Hide the caption while retaining `label` as the accessible name. */
  compact?: boolean;
  /**
   * Mark rung. `default` uses a 16px glyph in a 32px chip. `lg` uses a
   * 20px glyph in a 36px chip.
   */
  size?: AppShellRailTileSize;
  /** Apply the shared AI affordance ring to the chip. */
  aiRing?: boolean;
}> &
  Omit<BoxProps<"button">, "aria-label" | "as" | "children">;

/**
 * A vertical AppShell rail destination or action.
 *
 * The chip owns hover and active treatments. The optional caption follows the
 * rail presentation without changing its accessible name. Forwarded props let
 * overlay triggers merge their behavior onto the button.
 */
export function AppShellRailTile({
  icon,
  selectedIcon,
  visual,
  label,
  badge,
  badgeDot = false,
  active = false,
  compact = false,
  size = "default",
  aiRing = false,
  className,
  ...props
}: AppShellRailTileProps): React.ReactElement {
  const chipSize = APP_SHELL_RAIL_TILE_CHIP_SIZE[size];
  const iconSize = RAIL_TILE_ICON_SIZE[size];
  const renderedIcon = active && selectedIcon != null ? selectedIcon : icon;

  return (
    <Box<"button">
      align="center"
      aria-label={label}
      as="button"
      className={cn(
        active ? "text-sidebar-foreground" : "text-muted-foreground",
        className,
      )}
      data-slot="app-shell-rail-tile"
      fill="width"
      gap="2xs"
      group="tile"
      type="button"
      {...props}
    >
      <Box
        align="center"
        className={
          active
            ? "bg-primary text-primary-foreground"
            : // No transition here, deliberately. Any eased property gets caught
              // by the active→inactive class swap: `transition-colors` ghost-
              // faded the outgoing chip's solid fill, and an ink-only ease left
              // the glyph's on-chip ink stranded on the bare rail for 150ms
              // (dark-on-dark) after the fill snapped. State swaps snap whole —
              // Button's base eases shadow only, so hover ink snapping matches.
              "group-hover/tile:text-foreground"
        }
        data-ai-ring={aiRing ? "" : undefined}
        data-slot="app-shell-rail-tile-mark"
        height={chipSize}
        hoverBackground={!active}
        justify="center"
        position="relative"
        rounded="lg"
        width={chipSize}
      >
        {visual ??
          (renderedIcon != null ? (
            <Icon
              className="motion-safe:transition-transform motion-safe:duration-150 motion-safe:ease-out motion-safe:group-hover/tile:scale-110"
              name={renderedIcon}
              size={iconSize}
            />
          ) : null)}
        {badge != null && (
          // Inactive, the disc is the rail's ink on its fill; active, the chip
          // is painted `primary`, so the sticker inverts onto that pair.
          <CountSticker count={badge} dot={badgeDot} inverted={active} />
        )}
      </Box>
      {!compact && (
        <Text
          className="animate-fade-in motion-reduce:animate-none"
          size="2xs"
          truncate
          weight="medium"
        >
          {label}
        </Text>
      )}
    </Box>
  );
}
